import { Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import Production from "@/pages/Production";
import Inventory from "@/pages/Inventory";
import HandTerminal from "@/pages/HandTerminal";
import MaterialDetail from "@/pages/MaterialDetail";
import Suppliers from "@/pages/Suppliers";
import Purchases from "@/pages/Purchases";
import Reports from "@/pages/Reports";
import Departments from "@/pages/Departments";
import Products from "@/pages/Products";
import Warehouses from "@/pages/Warehouses";
import ProductDetail from "@/pages/ProductDetail";
import Tasks from "@/pages/Tasks";
import TasksAdmin from "@/pages/TasksAdmin";
import VariantSets from "@/pages/VariantSets";
import PurchaseRequirements from "@/pages/PurchaseRequirements";
import Settings from "@/pages/Settings";
import WhatsAppPage from "@/pages/WhatsAppPage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import DataDeletion from "@/pages/DataDeletion";
import Employees from "@/pages/Employees";
import EmployeeProfile from "@/pages/EmployeeProfile";
import EmployeePortal from "@/pages/EmployeePortal";
import Operations from "@/pages/Operations";
import FinanceDashboard from "@/pages/FinanceDashboard";
import Checks from "@/pages/Checks";
import CustomerProfile from "@/pages/CustomerProfile";
import SupplierProfile from "@/pages/SupplierProfile";
import OrderDetail from "@/pages/OrderDetail";
import OrderWizard from "@/pages/OrderWizard";
import Samples from "@/pages/Samples";
import Candidates from "@/pages/Candidates";
import GoodsReceipt from "@/pages/GoodsReceipt";
import InvoiceCheck from "@/pages/InvoiceCheck";
import PurchaseOrderPrint from "@/pages/PurchaseOrderPrint";
import Shipments from "@/pages/Shipments";
import OrdersList from "@/pages/OrdersList";
import { PdksPanel, PdksMovements, PdksTimesheet, PdksSettingsPage, PdksOvertime } from "@/pages/Pdks";
import { PayrollTimesheetPage, PayrollSalaryPage } from "@/pages/Payroll";
import Commissions from "@/pages/Commissions";
import LearningCenter, { WhatsNewBanner } from "@/pages/LearningCenter";
import CreditsPage from "@/pages/CreditsPage";
import SuperAdmin from "@/pages/SuperAdmin";
import DataMigration from "@/pages/DataMigration";
import SelectiveRestore from "@/pages/SelectiveRestore";
import AccountingHub from "@/pages/AccountingHub";
import KioskTerminal from "@/pages/KioskTerminal";
import PdksQrStamp, { PdksActivate } from "@/pages/PdksQrStamp";
import { PdksSetup, PdksApp } from "@/pages/PdksSetup";
import QuickSale from "@/pages/QuickSale";
import "@/App.css";

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Detect OAuth callback synchronously during render, BEFORE ProtectedRoute runs.
  // useEffect would be too late — the race would 401 on /auth/me first.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/gizlilik-politikasi" element={<PrivacyPolicy />} />
      <Route path="/veri-silme" element={<DataDeletion />} />
      <Route path="/pdks/terminal" element={<KioskTerminal />} />
      <Route path="/pdks/qr" element={<PdksQrStamp />} />
      <Route path="/pdks/aktivasyon" element={<PdksActivate />} />
      <Route path="/pdks/kurulum" element={<PdksSetup />} />
      <Route path="/pdks/app" element={<PdksApp />} />
      <Route
        path="/satinalma/po/:id/yazdir"
        element={
          <ProtectedRoute>
            <PurchaseOrderPrint />
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="musteri" element={<Customers />} />
        <Route path="uretim" element={<Production />} />
        <Route path="stok" element={<Inventory />} />
        <Route path="el-terminali" element={<HandTerminal />} />
        <Route path="stok/kumas/:id" element={<MaterialDetail type="fabric" />} />
        <Route path="stok/aksesuar/:id" element={<MaterialDetail type="accessory" />} />
        <Route path="tedarikci" element={<Suppliers />} />
        <Route path="satinalma" element={<Purchases />} />
        <Route path="raporlar" element={<Reports />} />
        <Route path="departmanlar" element={<Departments />} />
        <Route path="urunler" element={<Products />} />
        <Route path="depolar" element={<Warehouses />} />
        <Route path="urun/:id" element={<ProductDetail />} />
        <Route path="gorevler" element={<Tasks />} />
        <Route path="gorevler/yonetici" element={<TasksAdmin />} />
        <Route path="varyantlar" element={<VariantSets />} />
        <Route path="satin-alma-ihtiyaclari" element={<PurchaseRequirements />} />
        <Route path="mal-kabul" element={<GoodsReceipt />} />
        <Route path="fatura-kontrol" element={<InvoiceCheck />} />
        <Route path="ayarlar" element={<Settings />} />
        <Route path="whatsapp" element={<WhatsAppPage />} />
        <Route path="operasyonlar" element={<Operations />} />
        <Route path="cari" element={<FinanceDashboard />} />
        <Route path="cek-senet" element={<Checks />} />
        <Route path="calisan" element={<Employees />} />
        <Route path="ise-alim" element={<Candidates />} />
        <Route path="calisan/:id" element={<EmployeeProfile />} />
        <Route path="portal" element={<EmployeePortal />} />
        <Route path="musteri/:id" element={<CustomerProfile />} />
        <Route path="tedarikci/:id" element={<SupplierProfile />} />
        <Route path="teklif/:id" element={<OrderDetail mode="quotes" />} />
        <Route path="satis/:id" element={<OrderDetail mode="sales" />} />
        <Route path="siparis-sihirbazi" element={<OrderWizard />} />
        <Route path="numuneler" element={<Samples />} />
        <Route path="satislar" element={<OrdersList mode="sales" />} />
        <Route path="magaza/hizli-satis" element={<QuickSale />} />
        <Route path="teklifler" element={<OrdersList mode="quotes" />} />
        <Route path="sevkiyat" element={<Shipments />} />
        <Route path="pdks/panel" element={<PdksPanel />} />
        <Route path="pdks/hareketler" element={<PdksMovements />} />
        <Route path="pdks/puantaj" element={<PdksTimesheet />} />
        <Route path="pdks/fazla-mesai" element={<PdksOvertime />} />
        <Route path="pdks/ayarlar" element={<PdksSettingsPage />} />
        <Route path="puantaj/aylik" element={<PayrollTimesheetPage />} />
        <Route path="maas/hesaplama" element={<PayrollSalaryPage />} />
        <Route path="prim-komisyon" element={<Commissions />} />
        <Route path="egitim" element={<LearningCenter />} />
        <Route path="hesabim/krediler" element={<CreditsPage />} />
        <Route path="super-admin" element={<SuperAdmin />} />
        <Route path="super-admin/veri-migrasyonu" element={<DataMigration />} />
        <Route path="super-admin/secici-kurtarma" element={<SelectiveRestore />} />
        <Route path="ayarlar/entegrasyonlar/muhasebe" element={<AccountingHub />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <Suspense fallback={null}>
              <AppRouter />
            </Suspense>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
